// Pleasant "blup" bubble-pop sound played when a notification arrives live
// (see NotificationBell's onReceiveNotification effect) -- same spa/water
// language as the rest of the app's bubble visuals, just for the ear.
// Reuses one preloaded Audio instance instead of constructing a fresh one
// per notification, and silently swallows a blocked-autoplay rejection
// (same "nothing useful to do about it" catch as RealtimeService's own
// fire-and-forget hub calls) rather than surfacing a console error the user
// can't act on.
const NOTIFICATION_SOUND_URL = `${process.env.PUBLIC_URL}/sounds/notification-blup.wav`;
const NOTIFICATION_SOUND_VOLUME = 0.5;

let audio: HTMLAudioElement | null = null;

const getAudio = (): HTMLAudioElement => {
  if (!audio) {
    audio = new Audio(NOTIFICATION_SOUND_URL);
    audio.volume = NOTIFICATION_SOUND_VOLUME;
    // Starts fetching/decoding right away instead of waiting for the first
    // real play() call -- by the time a live notification actually needs
    // to play (see unlockOnFirstGesture below for the other half of this),
    // there's nothing left to wait on.
    audio.load();
  }
  return audio;
};

// Browsers require a real user gesture (click/tap/key) on this page at
// least once before allowing ANY audio to play -- there is no way around
// that, it's an intentional browser protection, not a bug. What WAS a bug:
// this only ever constructed+played the Audio element lazily, on the FIRST
// live notification that happened to arrive -- so if that first one showed
// up before the user had clicked anything yet (very plausible on this page;
// nothing about watching for a notification requires clicking first), the
// browser silently blocked it and the swallowed rejection made that look
// identical to "broken". Priming playback on the very first interaction
// ANYWHERE on the page (unrelated to notifications) instead means that by
// the time a real notification needs to play, the unlock has almost always
// already happened.
let unlocked = false;
const unlockOnFirstGesture = () => {
  if (unlocked) return;
  unlocked = true;
  const el = getAudio();
  const wasMuted = el.muted;
  el.muted = true;
  el.play()
    .then(() => {
      el.pause();
      el.currentTime = 0;
      el.muted = wasMuted;
    })
    .catch(() => {
      el.muted = wasMuted;
    });
};

if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', unlockOnFirstGesture, { once: true });
  document.addEventListener('keydown', unlockOnFirstGesture, { once: true });
}

export const playNotificationSound = (): void => {
  const el = getAudio();
  // Restart from the top -- a second notification arriving while the first
  // one's sound is still tailing off should re-trigger the "blup" rather
  // than being silently ignored because playback was already in progress.
  el.currentTime = 0;
  el.play().catch(() => undefined);
};
